import MainLayout from "../../../layout/MainLayout";
import { Button1, Select, ToggleButton } from "../../index";
import listIcon from "../../../assets/icons/list-icon.png";
import calendarIcon from "../../../assets/icons/calendar-icon.png";
import rightArrow from "../../../assets/icons/chevron-right.png";
import doubleRightArrow from "../../../assets/icons/chevron-right-double.png";
import leftArrow from "../../../assets/icons/chevron-left.png";
import doubleLeftArrow from "../../../assets/icons/chevron-left-double.png";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiPlus } from "react-icons/fi";
import { useBills } from "../../../data/live";
import QuickActionSelect from "../../quick-action-select/QuickActionSelect";

const BORDER_GRAY = "#E3E3E3";
const LABEL_GRAY = "#7D7D7D";
const BRAND_GREEN = "#1BBB6B";

const CATEGORY_COLOR = (cat) => {
  const palette = ["#1A73E8", "#FFB800", "#FC555B", "#9B59B6", "#FF6B35", "#00BCD4", "#E91E63"];
  let hash = 0;
  for (let i = 0; i < (cat || "").length; i++) hash = (hash * 31 + cat.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
};

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DayCell = ({ day, entries = [], outside, style }) => {
  const total = entries.reduce(
    (s, e) => s + (e.type === "income" ? e.amount : -e.amount),
    0
  );
  return (
    <div
      style={{
        borderRight: `1px solid ${BORDER_GRAY}`,
        borderBottom: `1px solid ${BORDER_GRAY}`,
        background: outside ? "#F8F9FB" : "#FFF",
        minHeight: 90,
        padding: 6,
        ...style,
      }}
    >
      <div className="d-flex justify-content-between align-items-start">
        <span
          className="fs12px fw-medium"
          style={{ color: outside ? "#BEBEBE" : "#161616" }}
        >
          {day}
        </span>
        <span
          className="fs12px"
          style={{
            color: total === 0 ? BRAND_GREEN : total > 0 ? BRAND_GREEN : "#161616",
          }}
        >
          {total !== 0 ? `$${Math.abs(total)}` : "$0"}
        </span>
      </div>
      {entries.map((e, i) => (
        <div
          key={i}
          className="d-flex justify-content-between align-items-center px-2 py-1 rounded mt-1"
          style={{ background: e.color, color: "#FFF", fontSize: 10 }}
        >
          <span>{e.label}</span>
          <span>${e.amount}</span>
        </div>
      ))}
      {!outside && (
        <div
          className="d-flex align-items-center gap-1 mt-1"
          style={{ color: BRAND_GREEN, fontSize: 11 }}
        >
          <FiPlus size={10} /> <span>Add New</span>
        </div>
      )}
    </div>
  );
};

const MonthlyGrid = ({ entriesByDay = {}, year, monthIdx }) => {
  const today = new Date();
  const useYear = year ?? today.getFullYear();
  const useMonth = monthIdx ?? today.getMonth();
  const FIRST_DOW = new Date(useYear, useMonth, 1).getDay();
  const daysInMonth = new Date(useYear, useMonth + 1, 0).getDate();
  const daysInPrevMonth = new Date(useYear, useMonth, 0).getDate();
  const cells = [];
  for (let i = 0; i < FIRST_DOW; i++) {
    cells.push({ day: daysInPrevMonth - FIRST_DOW + 1 + i, outside: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, outside: false });
  }
  let nextDay = 1;
  while (cells.length < 35) {
    cells.push({ day: nextDay++, outside: true });
  }
  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          background: "#F8F9FB",
        }}
      >
        {DAY_NAMES.map((d) => (
          <div
            key={d}
            className="p-2 text-center fw-medium"
            style={{ color: LABEL_GRAY, fontSize: 12, borderRight: `1px solid ${BORDER_GRAY}` }}
          >
            {d}
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {cells.map((c, i) => (
          <DayCell
            key={i}
            day={c.day}
            outside={c.outside}
            entries={c.outside ? [] : entriesByDay[c.day] || []}
          />
        ))}
      </div>
    </div>
  );
};

const WeeklyGrid = ({ entriesByDay = {} }) => {
  // Show the current 7-day window starting today
  const today = new Date();
  const startDay = today.getDate();
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
      {DAY_NAMES.map((d, i) => {
        const dayNum = startDay + i;
        return (
          <div key={d} style={{ background: "#F8F9FB" }}>
            <div
              className="p-2 text-center fw-medium"
              style={{ color: LABEL_GRAY, fontSize: 12 }}
            >
              {d}
            </div>
            <DayCell
              day={dayNum}
              entries={entriesByDay[dayNum] || []}
              style={{ minHeight: 300 }}
            />
          </div>
        );
      })}
    </div>
  );
};

const DailyGrid = () => {
  const hours = Array.from({ length: 10 }, (_, i) => `${i + 8}:00`);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "100px 1fr" }}>
      <div
        className="p-2 text-center fw-medium"
        style={{ background: "#F8F9FB", color: LABEL_GRAY, fontSize: 12 }}
      >
        Time
      </div>
      <div
        className="p-2 text-center fw-medium"
        style={{ background: "#F8F9FB", color: LABEL_GRAY, fontSize: 12 }}
      >
        Mon
      </div>
      {hours.map((h) => (
        <>
          <div
            key={h}
            className="p-2"
            style={{
              borderBottom: `1px solid ${BORDER_GRAY}`,
              color: LABEL_GRAY,
              fontSize: 12,
            }}
          >
            {h}
          </div>
          <div
            key={`${h}-cell`}
            style={{
              borderLeft: `1px solid ${BORDER_GRAY}`,
              borderBottom: `1px solid ${BORDER_GRAY}`,
              minHeight: 56,
              padding: 6,
            }}
          >
            <div
              className="d-flex align-items-center gap-1"
              style={{ color: BRAND_GREEN, fontSize: 11 }}
            >
              <FiPlus size={10} /> <span>Add New</span>
            </div>
          </div>
        </>
      ))}
    </div>
  );
};

const MultiWeekGrid = ({ cols = 8, labelPrefix = "Week", entriesByDay = {} }) => {
  const week = [1, 2, 3, 4, 5, 6, 7];
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {Array.from({ length: cols }).map((_, c) => (
        <div key={c}>
          <div
            className="p-2 text-center fw-medium"
            style={{ background: "#F8F9FB", color: LABEL_GRAY, fontSize: 12 }}
          >
            {labelPrefix} {c + 1}
          </div>
          {week.map((d) => (
            <DayCell
              key={`${c}-${d}`}
              day={d}
              entries={entriesByDay[d + c * 7] || []}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

const QuarterlyGrid = ({ entriesByDay = {} }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)" }}>
    {["Q1-2021", "Q2-2021", "Q3-2021", "Q4-2021", "Q1-2022", "Q2-2022", "Q3-2022", "Q4-2022"].map(
      (q, c) => (
        <div key={q}>
          <div
            className="p-2 text-center fw-medium"
            style={{ background: "#F8F9FB", color: LABEL_GRAY, fontSize: 12 }}
          >
            {q}
          </div>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
            <DayCell key={`${q}-${d}`} day={d} entries={entriesByDay[d] || []} />
          ))}
        </div>
      )
    )}
  </div>
);

const AnnuallyGrid = ({ entriesByDay = {} }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)" }}>
    {[2021, 2022, 2023, 2024, 2025, 2026].map((y, c) => (
      <div key={y}>
        <div
          className="p-2 text-center fw-medium"
          style={{ background: "#F8F9FB", color: LABEL_GRAY, fontSize: 12 }}
        >
          {y}
        </div>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((d) => (
          <DayCell key={`${y}-${d}`} day={d} entries={entriesByDay[d] || []} />
        ))}
      </div>
    ))}
  </div>
);

const CalendarGrid = ({ view, entriesByDay, year, monthIdx }) => {
  if (view.startsWith("Daily")) return <DailyGrid entriesByDay={entriesByDay} />;
  if (view.startsWith("Weekly")) return <WeeklyGrid entriesByDay={entriesByDay} />;
  if (view.startsWith("Fortnightly"))
    return <MultiWeekGrid cols={8} labelPrefix="Week" entriesByDay={entriesByDay} />;
  if (view.startsWith("Quarterly")) return <QuarterlyGrid entriesByDay={entriesByDay} />;
  if (view.startsWith("Annually")) return <AnnuallyGrid entriesByDay={entriesByDay} />;
  return <MonthlyGrid entriesByDay={entriesByDay} year={year} monthIdx={monthIdx} />;
};

const CalendarView = () => {
  const { bills } = useBills();
  const navigate = useNavigate();
  const [selectedCalendar, setSelectedCalendar] =
    useState("All Bills Calendar");
  const [monthOffset, setMonthOffset] = useState(0);

  const today = new Date();
  const viewYear = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1).getFullYear();
  const viewMonthIdx = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1).getMonth();

  /** Build entriesByDay from real bills for the current view month */
  const entriesByDay = useMemo(() => {
    const map = {};
    for (const b of bills || []) {
      const due = new Date(b.dueDate || b.createdAt);
      if (due.getFullYear() !== viewYear || due.getMonth() !== viewMonthIdx) continue;
      const dayNum = due.getDate();
      if (!map[dayNum]) map[dayNum] = [];
      map[dayNum].push({
        label: (b.billerName || b.category || "Bill").slice(0, 6) + (b.billerName?.length > 6 ? "..." : ""),
        amount: Number(b.amount || 0),
        color: b.status === "PAID" ? BRAND_GREEN : b.status === "OVERDUE" ? "#FC555B" : CATEGORY_COLOR(b.category || b.billerName || ""),
        type: "expense",
      });
    }
    return map;
  }, [bills, viewYear, viewMonthIdx]);

  const onToggle = (value) => {
    switch (value) {
      case "All Bills":
        setSelectedCalendar(`${value} Calendar`);
        break;
      case "Today":
        setSelectedCalendar(`${value} Calendar`);
        break;
      case "Weekly":
        setSelectedCalendar(`${value} Calendar`);
        break;
      case "Fortnightly":
        setSelectedCalendar(`${value} Calendar`);
        break;
      case "Montly":
        setSelectedCalendar(`${value} Calendar`);
        break;
      case "Quarterly":
        setSelectedCalendar(`${value} Calendar`);
        break;
      case "Annually":
        setSelectedCalendar(`${value} Calendar`);
        break;
      default:
        setSelectedCalendar("All Bills Calendar");
    }
  };
  return (
    <>
      <MainLayout NavName={"Manage My Spending"} expand={false}>
        <div>
          <div className="container mt-4">
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fs-5 fw-semibold">My Bills</span>
                <div className="d-flex align-items-center gap-2 ">
                  <QuickActionSelect />
                  <ToggleButton
                    defaultValue={"Calendar"}
                    options={[
                      {
                        value: "List",
                        icon: (
                          <Link to={"/biller"}>
                            <img src={listIcon} alt="list" />
                          </Link>
                        ),
                      },
                      {
                        value: "Calendar",
                        icon: <img src={calendarIcon} alt="calendar" />,
                      },
                    ]}
                  />
                </div>
              </div>

              <div className="d-flex flex-column gap-3 border rounded-3">
                <div className="d-flex justify-content-between p-3 px-5 border-bottom align-items-center flex-wrap gap-2">
                  <div className="d-flex gap-3 align-items-center">
                    <span className="fs-5 fw-semibold ">All Bills</span>

                    <div className="d-flex gap-2 align-items-center align-self-end">
                      <img
                        src={doubleRightArrow}
                        alt="doubleRight"
                        className="cursor-pointer"
                        onClick={() => setMonthOffset((o) => o + 12)}
                      />
                      <img
                        src={rightArrow}
                        alt="right"
                        className="cursor-pointer"
                        onClick={() => setMonthOffset((o) => o + 1)}
                      />
                      <img
                        src={leftArrow}
                        alt="left"
                        className="cursor-pointer"
                        onClick={() => setMonthOffset((o) => o - 1)}
                      />
                      <img
                        src={doubleLeftArrow}
                        alt="double"
                        className="cursor-pointer"
                        onClick={() => setMonthOffset((o) => o - 12)}
                      />
                    </div>
                  </div>
                  <div style={{ width: "60%" }}>
                    <ToggleButton
                      options={[
                        "All Bills",
                        "Today",
                        "Weekly",
                        "Fortnightly",
                        "Montly",
                        "Quarterly",
                        "Annually",
                      ]}
                      onChange={(value) => onToggle(value)}
                      block={true}
                    />
                  </div>
                </div>

                <CalendarGrid view={selectedCalendar} entriesByDay={entriesByDay} year={viewYear} monthIdx={viewMonthIdx} />

                {selectedCalendar === "All Bills Calendar" && (
                  <div className="d-flex justify-content-end align-items-center gap-2 p-3 px-5">
                    <Button1
                      buttonName={"Back to My Bills"}
                      className={"border"}
                      onClick={() => navigate("/biller")}
                    />
                    <Button1
                      buttonName={"Add Billers"}
                      className={"btn-dark"}
                      onClick={() => navigate("/addbiller")}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  );
};

export default CalendarView;
